import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, X, StickyNote } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { LinkTypeChip } from "@/components/TaskBadge";

const STATUSES = ["Open", "In Progress", "Completed", "Cancelled"];
const PRIORITIES = ["High", "Medium", "Low"];
const LINK_TYPES = ["Asset", "Lead", "Broker"];

const ENDPOINT = { Asset: "/assets", Lead: "/leads", Broker: "/brokers" };
function recordName(type, r) {
  if (type === "Asset") return { name: r.name, code: r.asset_code };
  if (type === "Lead") return { name: r.lead_name, code: r.lead_code };
  return { name: r.broker_name, code: null };
}

function RecordPicker({ type, valueId, valueName, onPick }) {
  const [q, setQ] = useState("");
  const { data = [] } = useQuery({
    queryKey: ["task-link-search", type, q],
    queryFn: async () => (await api.get(ENDPOINT[type], { params: q ? { q } : {} })).data,
    enabled: !!type,
  });
  const filtered = (Array.isArray(data) ? data : [])
    .map((r) => ({ id: r.id, ...recordName(type, r) }))
    .filter((r) => !q || (r.name || "").toLowerCase().includes(q.toLowerCase()))
    .slice(0, 8);

  if (valueId) {
    return (
      <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid="task-linked-selected">
        <span className="text-sm text-slate-800 truncate">{valueName || valueId}</span>
        <button type="button" onClick={() => onPick(null, null)} className="text-slate-400 hover:text-slate-700" data-testid="task-linked-clear">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${type}…`} className="pl-8" data-testid="task-link-search" />
      </div>
      {q && (
        <div className="mt-1 border border-slate-200 rounded-md max-h-44 overflow-y-auto divide-y divide-slate-50">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">No matches</div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onPick(r.id, r.name); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                data-testid={`task-link-option-${r.id}`}
              >
                <span className="font-medium text-slate-900">{r.name}</span>
                {r.code && <span className="ml-2 font-mono-num text-[10px] text-slate-400">{r.code}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function TaskFormDialog({ open, onClose, task, prefillLink, prefill, onCreated }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || (user?.roles || []).includes("admin");
  const editing = !!task;

  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm({
      title: task?.title === "Untitled Task" ? "" : task?.title || prefill?.title || "",
      description: task?.description || prefill?.description || "",
      status: task?.status || "Open",
      priority: task?.priority || "Medium",
      assigned_to: task?.assigned_to || user?.id,
      linked_record_type: task?.linked_record_type || prefill?.linked_record_type || prefillLink?.type || null,
      linked_record_id: task?.linked_record_id || prefill?.linked_record_id || prefillLink?.id || null,
      linked_record_name: task?.linked_record_name || prefill?.linked_record_name || prefillLink?.name || null,
      due_date: task?.due_date || prefill?.due_date || null,
      reminder_datetime: task?.reminder_datetime || null,
    });
  }, [open, task, prefillLink, prefill, user]);

  const { data: users = [] } = useQuery({
    queryKey: ["task-assignees"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: open && isAdmin,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        priority: form.priority,
        assigned_to: form.assigned_to,
        linked_record_type: form.linked_record_type,
        linked_record_id: form.linked_record_type ? form.linked_record_id : null,
        due_date: form.due_date,
        reminder_datetime: form.reminder_datetime,
        ...(!editing && prefill?.source_note_id ? { source_note_id: prefill.source_note_id } : {}),
      };
      if (editing) return (await api.patch(`/tasks/${task.id}`, payload)).data;
      return (await api.post("/tasks", payload)).data;
    },
    onSuccess: (data) => {
      toast.success(editing ? "Task updated" : "Task created");
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["record-tasks"] });
      qc.invalidateQueries({ queryKey: ["action-center"] });
      qc.invalidateQueries({ queryKey: ["tasks-badge-count"] });
      if (!editing && onCreated) onCreated(data);
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const reminderAfterDue =
    form.reminder_datetime && form.due_date &&
    new Date(form.reminder_datetime) > new Date(`${form.due_date}T23:59:59`);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription>Track an action and optionally link it to an asset, lead or broker.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {editing && task?.source_note_id && (
            task.source_note_deleted ? (
              <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-400" data-testid="task-from-note-deleted">
                <StickyNote className="h-3.5 w-3.5" /> From note <span className="italic">(deleted)</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { navigate(`/notes?highlight=${task.source_note_id}`); onClose(); }}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-rose-100 bg-rose-50 px-3 py-1.5 text-xs text-rose-700 hover:opacity-80"
                data-testid="task-from-note"
              >
                <StickyNote className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">From note: {task.source_note_label || "View note"}</span>
              </button>
            )
          )}
          <div>
            <Label>Title</Label>
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="What needs doing?" data-testid="task-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Optional details…" data-testid="task-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`task-status-opt-${s}`}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Assigned To</Label>
            {isAdmin ? (
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={user?.name || "Me"} disabled data-testid="task-assignee-self" />
            )}
          </div>
          <div>
            <Label>Link to Record</Label>
            <div className="grid grid-cols-3 gap-2">
              <Select
                value={form.linked_record_type || "none"}
                onValueChange={(v) => { set("linked_record_type", v === "none" ? null : v); set("linked_record_id", null); set("linked_record_name", null); }}
              >
                <SelectTrigger data-testid="task-link-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No link</SelectItem>
                  {LINK_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`task-link-type-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="col-span-2">
                {form.linked_record_type ? (
                  <RecordPicker
                    type={form.linked_record_type}
                    valueId={form.linked_record_id}
                    valueName={form.linked_record_name}
                    onPick={(id, name) => { set("linked_record_id", id); set("linked_record_name", name); }}
                  />
                ) : (
                  <Input disabled placeholder="Select a type first" />
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due Date</Label>
              <DatePicker value={form.due_date} onChange={(v) => set("due_date", v)} testId="task-due-date" placeholder="No due date" />
            </div>
            <div>
              <Label>Reminder</Label>
              <Input
                type="datetime-local"
                value={toLocalInput(form.reminder_datetime)}
                onChange={(e) => set("reminder_datetime", e.target.value ? new Date(e.target.value).toISOString() : null)}
                data-testid="task-reminder-datetime"
              />
            </div>
          </div>
          {reminderAfterDue && (
            <div className="text-[11px] text-amber-600" data-testid="task-reminder-warning">
              Reminder is set after the due date.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="task-cancel">Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="task-save">
            {save.isPending ? "Saving…" : editing ? "Save changes" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
